import React, { useContext, useEffect, useState } from "react";
import { Animated, StyleSheet, View, Dimensions } from "react-native";
import { Modal, Text, TouchableOpacity } from "react-native";
import { ScrollView } from "react-native-gesture-handler";
import Spinner from "../../../components/shared/Spinner";
import DeviceOrientationContext from "../../../components/context/DeviceOrientationContext";
import { useDispatch, useSelector } from "react-redux";
import { LabelBannerWithSearchBar } from "../../../components/custom/Banners/LabelBannerWithSearchBar";
import { useIsFocused } from "@react-navigation/native";
import {
  translateFields,
  translateHeaders,
  useTranslation,
} from "../../../utils/useTranslation";
import { chinaTrackerTranslationMap } from "../../../utils/translations";
import { ChinaTrackerDataTableWithPagination2 } from "../../../components/custom/DataTable/tracker/ChinaTrackerDataTableWithPagination2";
import {
  resetAllSearch,
  searchAllTrackerOrders,
} from "../../../redux/reducers/chinaTrackerReducer";

interface TrackerEnquiryModalProps {
  visible: boolean;
  onClose: any;
  order: string;
}

// Define headers for the data table
const tableHeadersData = {
  MSRef: {
    name: "MS No.",
    width: 0.3,
    fontWeight: 700,
  },
  OrderNumber1: {
    name: "Order Number",
    width: 0.3,
    fontWeight: 700,
  },
  ItemCode: {
    name: "Item Code",
    width: 0.3,
  },
  ItemName: {
    name: "Item Name",
    width: 0.8,
  },
  StateDelivery: {
    name: "State",
    width: 0.2,
    fontWeight: 700,
  },
  OrderDate: {
    name: "PO Order Date",
    width: 0.3,
    fontWeight: 700,
  },
  PriorityOrder: {
    name: "Priority Order",
    width: 0.3,
    fontWeight: 700,
  },
  RequestDate: {
    name: "Due Date",
    width: 0.3,
    fontWeight: 700,
  },
  TotalOrdered: {
    name: "Order Qty (UoM)",
    width: 0.3,
    fontWeight: 700,
  },
  OrderStatus: {
    name: "Status",
    width: 0.4,
    fontWeight: 700,
  },
  incomingStatus: {
    name: "Incoming Status",
    width: 0.4,
    fontWeight: 700,
  },
};

const ChinaTrackerAdvancedSearch: React.FC<TrackerEnquiryModalProps> = ({
  visible,
  onClose,
  order,
}: any) => {
  const [screenWidth, setScreenWidth] = useState(
    Dimensions.get("window").width,
  );
  const [screenHeight, setScreenHeight] = useState(
    Dimensions.get("window").height,
  );
  const [tableHeaders, setTableHeaders] =
    useState<Partial<typeof tableHeadersData>>(tableHeadersData);

  const {
    allOrders: orders,
    loading,
    loadingIncomingAdd,
  } = useSelector((state: any) => state.chinaTracker);
  const { t } = useTranslation();
  const [isSearched, setIsSearched] = useState<boolean>(false);
  const [page, setPage] = useState<number>(0);
  const dispatch = useDispatch<any>();
  const [actionRows, setActionRows] = useState<any>([]);
  const [isRefresh, setRefresh] = useState<boolean>(false);
  const { isLargeDevice, isMediumDevice, isSmallDevice } = useContext(
    DeviceOrientationContext,
  );
  const [tableScrollOffset, setTableScrollOffset] = useState(0);

  // Update dimensions when screen size changes
  useEffect(() => {
    const updateDimensions = () => {
      const { width, height } = Dimensions.get("window");
      setScreenWidth(width);
      setScreenHeight(height);
    };

    const dimensionsListener = Dimensions.addEventListener(
      "change",
      updateDimensions,
    );

    return () => {
      dimensionsListener.remove();
    };
  }, []);

  // Calculate modal width based on screen size
  const getModalWidth = () => {
    if (isSmallDevice) return "100%";
    if (isMediumDevice) return "94%";
    return "92%";
  };

  // Calculate inner container width based on screen size
  const getInnerContainerWidth = () => {
    if (isSmallDevice) return "100%";
    return "100%";
  };

  const handleSearch = (searchedText: string) => {
    setPage(0);
    if (searchedText) {
      dispatch(searchAllTrackerOrders(searchedText));
    } else {
      dispatch(resetAllSearch());
    }
  };
  const isFocused = useIsFocused();

  useEffect(() => {
    let isMounted: boolean = true;
    setRefresh(false);

    return () => {
      isMounted = false;
    };
  }, [isRefresh, isLargeDevice, isMediumDevice, isSmallDevice, screenWidth]);

  useEffect(() => {
    setRefresh(false);
    setTableHeaders(
      translateHeaders(tableHeadersData, t, chinaTrackerTranslationMap),
    );
  }, [isFocused, isRefresh]);

  useEffect(() => {
    setTableHeaders(
      translateHeaders(tableHeadersData, t, chinaTrackerTranslationMap),
    );
  }, []);

  return (
    <>
      <Modal visible={visible} transparent animationType="slide">
        <View style={styles.overlay}>
          {isLargeDevice ? (
            <View style={[styles.modalContainer, { width: getModalWidth() }]}>
              <View
                style={[
                  styles.modalContainerSecond,
                  { width: getInnerContainerWidth(), height: "100%" },
                ]}
              >
                <ScrollView
                  style={styles.modalContentScroll}
                  contentContainerStyle={styles.modalContentScrollContainer}
                  stickyHeaderIndices={[0]}
                >
                  <View style={styles.stickyHeaderSection}>
                    <View
                      style={[
                        styles.titleContainer,
                        isSmallDevice && {
                          flexDirection: "column",
                        },
                      ]}
                    >
                      <Text
                        style={[
                          styles.titleText,
                          isSmallDevice && styles.titleTextSmall,
                        ]}
                      >
                        {t("allOrders")}
                      </Text>
                      {!isSmallDevice && (
                        <View style={[styles.searchContainer]}>
                          <LabelBannerWithSearchBar
                            titleName={``}
                            handleSearch={handleSearch}
                            setRefresh={setRefresh}
                            dataToExport={orders}
                            fileName="ChinaTrackerAllOrders"
                            sheetName="China Tracker All Orders Screen"
                          />
                        </View>
                      )}
                    </View>
                  </View>

                  <View style={styles.scrollBodySection}>
                    <View style={styles.tableScrollWrapper}>
                      <Animated.ScrollView
                        horizontal
                        showsHorizontalScrollIndicator
                        contentContainerStyle={{
                          minWidth: Math.max(screenWidth, 1200),
                        }}
                        scrollEventThrottle={16}
                        onScroll={(event) =>
                          setTableScrollOffset(
                            event.nativeEvent.contentOffset.x || 0,
                          )
                        }
                      >
                        <View
                          style={[
                            styles.containerContent,
                            { minWidth: Math.max(screenWidth, 1200) },
                          ]}
                        >
                          {!loading || !loadingIncomingAdd ? (
                            <View>
                              <ChinaTrackerDataTableWithPagination2
                                headers={tableHeaders}
                                rows={orders}
                                itemsPerPage={10}
                                isDataLoading={isSearched}
                                isDisplayTimeWithDate={true}
                                useModal={true}
                                isDetail={false}
                                isEdit={false}
                                isDelete={false}
                                actionRows={actionRows}
                                setActionRows={setActionRows}
                                itemTracker
                                // Required props
                                setModalVisible={() => {}}
                                handleRowClick={() => {}}
                                setSelected={() => {}}
                                handleDeleteRow={() => {}}
                                operationActions={() => {}}
                                selectable={false}
                                nestedHeader={null}
                                t={t}
                                page={page}
                                setPage={setPage}
                              />
                            </View>
                          ) : (
                            <View>
                              <Spinner
                                size="small"
                                color="#74BBFF"
                                styles={{ flexDirection: "row", margin: "20%" }}
                              />
                            </View>
                          )}
                        </View>
                      </Animated.ScrollView>
                    </View>
                    <View
                      style={[
                        styles.footer,
                        isSmallDevice && styles.footerSmall,
                      ]}
                    >
                      <TouchableOpacity
                        onPress={onClose}
                        style={[
                          styles.closeButton,
                          isSmallDevice && styles.buttonSmall,
                        ]}
                      >
                        <Text style={isSmallDevice && styles.buttonTextSmall}>
                          {t("close")}
                        </Text>
                      </TouchableOpacity>
                    </View>
                  </View>
                </ScrollView>
              </View>
            </View>
          ) : (
            <View
              style={[
                styles.modalContainerSecond,
                { width: getInnerContainerWidth(), height: "90%" },
              ]}
            >
              <ScrollView
                style={styles.modalContentScroll}
                contentContainerStyle={styles.modalContentScrollContainer}
              >
                <View
                  style={[
                    styles.titleContainer,
                    isSmallDevice && {
                      flexDirection: "column",
                    },
                  ]}
                >
                  <Text
                    style={[
                      styles.titleText,
                      isSmallDevice && styles.titleTextSmall,
                    ]}
                  >
                    {t("allOrders")}
                  </Text>
                  {!isSmallDevice && (
                    <View style={[styles.searchContainer]}>
                      <LabelBannerWithSearchBar
                        titleName={``}
                        handleSearch={handleSearch}
                        setRefresh={setRefresh}
                        dataToExport={orders}
                        fileName="ChinaTrackerRejectedOrders"
                        sheetName="China Tracker Reject Report Screen"
                      />
                    </View>
                  )}
                </View>

                <View style={styles.actionButtonsContainer}>
                  <Text style={{ display: "flex", alignItems: "center" }}>
                    {actionRows?.length}/10{" "}
                    {translateFields(
                      "ItemsSelected",
                      t,
                      chinaTrackerTranslationMap,
                    )}
                  </Text>
                  <TouchableOpacity
                    style={
                      actionRows.length > 0
                        ? styles.actionButton
                        : styles.disabledActionButton
                    }
                    disabled={actionRows.length > 0 ? false : true}
                    onPress={() => {}}
                  >
                    <Text style={styles.actionButtonText}>
                      {translateFields("Revise", t, chinaTrackerTranslationMap)}
                    </Text>
                  </TouchableOpacity>
                </View>

                <View style={styles.scrollBodySection}>
                  <View style={styles.tableScrollWrapper}>
                    <Animated.ScrollView
                      horizontal
                      showsHorizontalScrollIndicator
                      contentContainerStyle={{
                        minWidth: Math.max(screenWidth, 1200),
                      }}
                      scrollEventThrottle={16}
                      onScroll={(event) =>
                        setTableScrollOffset(
                          event.nativeEvent.contentOffset.x || 0,
                        )
                      }
                    >
                      <View
                        style={[
                          styles.containerContent,
                          { minWidth: Math.max(screenWidth, 1200) },
                        ]}
                      >
                        {!loading || !loadingIncomingAdd ? (
                          <View>
                            <ChinaTrackerDataTableWithPagination2
                              headers={tableHeaders}
                              rows={orders}
                              itemsPerPage={10}
                              isDataLoading={isSearched}
                              isDisplayTimeWithDate={true}
                              useModal={true}
                              isDetail={false}
                              isEdit={false}
                              isDelete={false}
                              actionRows={actionRows}
                              setActionRows={setActionRows}
                              itemTracker
                              // Required props
                              setModalVisible={() => {}}
                              handleRowClick={() => {}}
                              setSelected={() => {}}
                              handleDeleteRow={() => {}}
                              page={page}
                              setPage={setPage}
                            />
                          </View>
                        ) : (
                          <View>
                            <Spinner
                              size="small"
                              color="#74BBFF"
                              styles={{ flexDirection: "row", margin: "20%" }}
                            />
                          </View>
                        )}
                      </View>
                    </Animated.ScrollView>
                  </View>
                  <View
                    style={[styles.footer, isSmallDevice && styles.footerSmall]}
                  >
                    <TouchableOpacity
                      onPress={onClose}
                      style={[
                        styles.closeButton,
                        isSmallDevice && styles.buttonSmall,
                      ]}
                    >
                      <Text style={isSmallDevice && styles.buttonTextSmall}>
                        {t("close")}
                      </Text>
                    </TouchableOpacity>
                  </View>
                </View>
              </ScrollView>
            </View>
          )}
        </View>
      </Modal>
    </>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(0,0,0,0.15)",
    paddingVertical: 16,
    paddingHorizontal: 8,
  },
  modalContainer: {
    backgroundColor: "#fff",
    padding: 20,
    elevation: 12,
    borderRadius: 12,
    width: "100%",
    maxHeight: "95%",
  },
  modalContainerSecond: {
    backgroundColor: "#fff",
    elevation: 12,
    borderRadius: 12,
    padding: 16,
    width: "100%",
    height: "95%",
  },
  titleContainer: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    borderBottomWidth: 1,
    borderBottomColor: "#E5E7EB",
    backgroundColor: "#FFFFFF",
    paddingVertical: 12,
    marginBottom: 12,
  },
  titleText: {
    fontSize: 18,
    fontWeight: "700",
  },
  titleTextSmall: {
    fontSize: 14,
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
  },
  searchInput: {
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 4,
    padding: 8,
    width: 200,
  },
  searchButton: {
    backgroundColor: "#f0f0f0",
    padding: 8,
    borderRadius: 4,
    marginLeft: 5,
  },
  actionButtonsContainer: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginBottom: 12,
    gap: 10,
  },
  actionButton: {
    backgroundColor: "#56B7FD",
    paddingVertical: 8,
    paddingHorizontal: 15,
    borderRadius: 5,
    marginLeft: 10,
    width: 67,
  },
  disabledActionButton: {
    backgroundColor: "#D8D8D8",
    paddingVertical: 8,
    paddingHorizontal: 15,
    borderRadius: 5,
    marginLeft: 10,
    width: 67,
  },
  actionButtonText: {
    fontSize: 10,
    color: "#fff",
    fontWeight: 700,
  },
  formSection: {
    marginTop: 20,
    paddingHorizontal: 10,
  },
  footer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    alignItems: "center",
    marginTop: 20,
    padding: 25,
  },
  footerSmall: {
    flexDirection: "row",
    justifyContent: "flex-start",
  },
  closeButton: {
    padding: 10,
    borderWidth: 1,
    borderColor: "#ccc",
    borderRadius: 5,
    width: 120,
    alignItems: "center",
  },
  buttonSmall: {
    padding: 8,
    paddingHorizontal: 12,
  },
  buttonTextSmall: {
    fontSize: 12,
  },
  containerContent: {
    flexDirection: "column",
    alignSelf: "flex-start",
    width: "100%",
    paddingHorizontal: 0,
    paddingBottom: 0,
  },
  tableScrollWrapper: {
    flex: 1,
    width: "100%",
  },
  modalContentScroll: {
    flex: 1,
  },
  modalContentScrollContainer: {
    paddingBottom: 20,
  },
  stickyHeaderSection: {
    backgroundColor: "#fff",
    paddingBottom: 12,
    zIndex: 10,
  },
  scrollBodySection: {
    paddingTop: 12,
  },
});

export default ChinaTrackerAdvancedSearch;
